<script setup>
import { sendContactFormToServer } from "~/services/emailSendingServices.ts"
import { SUPPORT_EMAIL } from "~/services/constants"

const localePath = useLocaleRoute()
const { $swal } = useNuxtApp()
const router = useRouter()
const route = useRoute()

defineI18nRoute({
  paths: {
    de: "/kontakt",
    en: "/contact",
  },
})

// data
const email = ref("")
const content = ref("")

const config = useNuxtApp().$config

onMounted(() => {
  const redirectQueryParam = route.query.redirect
  if (redirectQueryParam) {
    content.value += "Ein Problem lag vor, ich fand mich auf einer leeren Seite wieder! Seite: " + redirectQueryParam
  }
})

// methods
async function avoidFormSubmitAndPost(e) {
  e.preventDefault()
  const res = await sendContactFormToServer(email.value, content.value, config.public.backendBaseUrl)
  if (res === false) {
    alertFailure()
  } else {
    alertSuccess()
  }
}

function alertFailure() {
  $swal.fire({
    title: "Ein Fehler ist aufgetreten",
    text: "Das tut uns leid! Bitten Sie es noch einmal oder schreiben Sie uns eine E-Mail an: support@memodio-app.com!",
    icon: "error",
    confirmButtonText: "Verstanden",
  })
}

function alertSuccess() {
  $swal
    .fire({
      title: "Wir haben Ihre Nachricht erhalten",
      text:
        "Vielen Dank! Wir haben Ihre Nachricht von der E-Mail-Adresse: " +
        email.value +
        " erhalten und werden uns zeitnah mit Ihnen in Verbindung setzen.",
      icon: "success",
      confirmButtonText: "Zurück zur Startseite",
    })
    .then(result => {
      if (result.isConfirmed) {
        router.push(localePath("/"))
      }
    })
}
</script>

<template>
  <div class="contact-form">
    <div class="no-background container is-max-desktop">
      <div class="content mx-4">
        <div class="columns reverse-it">
          <div class="column is-three-fifths">
            <div class="title">
              <h1 class="is-size-2 is-size-3-mobile">
                {{ $t("contact.form") }}
              </h1>
            </div>
            <div class="description">
              <form id="contact-form" @submit.prevent="avoidFormSubmitAndPost">
                <div class="form-group">
                  <p class="is-size-5 is-size-6-mobile">
                    {{ $t("contact.pleaseWrite") }}
                    <br />
                    <br />
                    {{ $t("contact.perMail") }}
                    <b>{{ SUPPORT_EMAIL }}</b>
                    <br />
                    {{ $t("contact.or") }}
                    <br /><br />
                  </p>
                </div>
                <div class="form-group">
                  <div class="email-container">
                    <label class="is-size-5 is-size-6-mobile e-mail" for="_replyto">
                      {{ $t("contact.yourMail") }}:
                    </label>
                    <input
                      id="_replyto"
                      v-model="email"
                      required
                      class="form-control input is-primary"
                      name="_replyto"
                      type="email"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <div class="message-container">
                    <label class="is-size-5 is-size-6-mobile">
                      {{ $t("contact.yourMessage") }}
                    </label>
                    <textarea
                      v-model="content"
                      required
                      class="form-control message-text is-size-5 is-size-6-mobile"
                      name="message"
                      rows="12"
                    />
                  </div>
                </div>
                <p class="is-size-7 my-3">
                  {{ $t("contact.dataProtection") }}
                  <a href="/pdfs/memodio-Datenschutzhinweise-Bewerber.pdf" target="_blank" download>
                    {{ $t("contact.downloadHere") }}
                  </a>
                  <span v-if="$i18n.locale === 'de'">finden</span>.
                </p>

                <div class="form-group mt-4">
                  <button
                    class="btn edudoc-color button is-rounded is-large is-responsive is-brandpurple"
                    type="submit"
                  >
                    {{ $t("contact.submit") }}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div class="column is-two-fifths">
            <img
              src="~assets/images/memodio_logos/logo_icon_purple.svg"
              class="memodio-logo"
              :alt="$t('contact.alt.logo')"
            />
            <h2 class="is-size-3">memodio GmbH</h2>
            <p class="is-size-6 has-text-weight-500">
              memodio GmbH <br />
              c/o Potsdam Transfer August-Bebel-Str. 89<br />
              14482 Potsdam <br />
              Deutschland <br /><br />
            </p>
            <p class="is-size-6 has-text-weight-500">
              Registergericht: Amtsgericht Potsdam <br />
              Registernummer HRB 36139 P<br />
              USt-ID.: DE 351 312 294 <br /><br />
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ContactForm",
}
</script>

<style lang="scss" scoped>
.form-control {
  color: $brandpurple;
}

.e-mail {
  width: 60%;
}

.message-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.message-text {
  border-radius: 4px;
  padding: 4px 4px 4px 4px;
}

.message-text:focus {
  box-shadow: 0 0 0 0.125em rgb(59 63 74 / 25%);
}

.message-text:focus-visible {
  outline: none;
}

.email-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.memodio-logo {
  width: 50%;
}

.reverse-it {
  flex-direction: row-reverse;
}

.contact-form {
  padding-top: 80px;
  padding-bottom: 80px;
}

.has-text-weight-500 {
  font-weight: 500 !important;
}

.has-text-weight-700 {
  font-weight: 700 !important;
}

p {
  line-height: 22px;
}
</style>
